import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { academyPublicAPI } from '../../lib/academyApi';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, Users, MapPin, Loader2,
  BookOpen, GraduationCap, Target, ChevronRight, CheckCircle,
  Globe, CreditCard, Calendar, Video, Award, FileText, User, Send,
} from 'lucide-react';

const modeLabel = { presentiel: 'Presentiel', distance: 'A distance (Teams)', hybride: 'Hybride' };

const TrainingDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', company: '',
    professional_order_number: '', num_participants: 1, session_id: '',
    payment_method: 'stripe', comments: '',
  });

  useEffect(() => {
    Promise.all([
      academyPublicAPI.getTraining(slug),
      academyPublicAPI.getSettings(),
    ]).then(([tRes, sRes]) => {
      setTraining(tRes.data);
      setSettings(sRes.data);
    }).catch(() => navigate('/academie'))
    .finally(() => setLoading(false));
  }, [slug, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) { toast.error('Prenom, nom et courriel requis'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, training_id: training.id };
      if (!payload.session_id) delete payload.session_id;
      const res = await academyPublicAPI.register(payload);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success(res.data.message || 'Inscription enregistree!');
        setShowForm(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" /></div>;
  if (!training) return null;

  const price = training.promo_price || training.price;
  const sessions = training.sessions || [];
  const instructors = training.instructors || [];

  return (
    <div data-testid="training-detail-page">
      {/* Hero */}
      <section className="relative bg-[#1D1D1B] py-16 lg:py-20">
        {training.image_url && <div className="absolute inset-0 opacity-15" style={{backgroundImage:`url("${training.image_url}")`,backgroundSize:'cover',backgroundPosition:'center'}} />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1D1D1B] via-[#1D1D1B]/95 to-[#1D1D1B]/70" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <Link to="/academie" className="inline-flex items-center gap-2 text-white/60 hover:text-[#FFD537] mb-6 transition-colors"><ArrowLeft className="w-4 h-4" /> Retour au catalogue</Link>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {training.category_name && <Badge className="mb-3 text-[#1D1D1B]" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>{training.category_name}</Badge>}
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">{training.title}</h1>
              <p className="text-lg text-white/70 mb-6">{training.short_description}</p>
              <div className="flex flex-wrap gap-4 text-sm text-white/60">
                {training.duration && <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{training.duration}</span>}
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{modeLabel[training.mode]}</span>
                {training.language && <span className="flex items-center gap-1"><Globe className="w-4 h-4" />{training.language}</span>}
                {training.certification && <span className="flex items-center gap-1"><Award className="w-4 h-4" />{training.certification}</span>}
              </div>
            </div>
            {/* Price Card */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/10">
              <CardContent className="p-6 text-center">
                {training.promo_price ? (
                  <><p className="text-4xl font-bold text-[#FFD537]">{training.promo_price} $</p><p className="text-white/40 line-through">{training.price} $</p></>
                ) : price > 0 ? (
                  <p className="text-4xl font-bold text-[#FFD537]">{price} $</p>
                ) : (
                  <p className="text-4xl font-bold text-green-400">Gratuit</p>
                )}
                <p className="text-white/60 text-sm mt-1">par participant + taxes</p>
                {training.duration_hours && <p className="text-white/40 text-xs mt-2">{training.duration_hours} heures de formation</p>}
                <Button onClick={() => setShowForm(true)} className="w-full mt-4" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)', color:'#1D1D1B'}} data-testid="register-btn">
                  <Send className="w-4 h-4 mr-2" />S'inscrire
                </Button>
                {sessions.length > 0 && <p className="text-[#FFD537] text-xs mt-2">{sessions.length} session(s) disponible(s)</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-10">
              {/* Description */}
              {training.full_description && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#1D1D1B] mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#F9B438]" />Description</h2>
                  <p className="text-neutral-600 whitespace-pre-line leading-relaxed">{training.full_description}</p>
                </div>
              )}

              {/* Objectives */}
              {training.objectives?.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#1D1D1B] mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-[#F9B438]" />Objectifs d'apprentissage</h2>
                  <ul className="space-y-3">
                    {training.objectives.map((obj, i) => (
                      <li key={i} className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#F9B438] mt-0.5 flex-shrink-0" /><span className="text-neutral-600">{obj}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Programme / Outline */}
              {training.outline?.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#1D1D1B] mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-[#F9B438]" />Contenu de la formation</h2>
                  <div className="space-y-4">
                    {training.outline.map((section, i) => (
                      <Card key={i} className="border-l-4 border-l-[#F9B438]">
                        <CardContent className="p-5">
                          <h3 className="font-heading font-bold text-[#1D1D1B] mb-3 flex items-center gap-2">
                            <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-[#1D1D1B] flex-shrink-0" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>{i + 1}</span>
                            {section.title}
                          </h3>
                          <ul className="space-y-2 ml-9">
                            {section.items?.map((item, j) => (
                              <li key={j} className="flex items-start gap-2">
                                <ChevronRight className="w-4 h-4 text-[#F9B438] mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="text-neutral-700">{item.title}</span>
                                  {item.description && <p className="text-sm text-neutral-400 mt-0.5">{item.description}</p>}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Training materials */}
              {training.training_materials && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#1D1D1B] mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#F9B438]" />Materiel de formation</h2>
                  <p className="text-neutral-600 whitespace-pre-line">{training.training_materials}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Audience */}
              {training.target_audience && (
                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-heading font-bold text-[#1D1D1B] mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#F9B438]" />Public cible</h3>
                    <p className="text-sm text-neutral-600">{training.target_audience}</p>
                  </CardContent>
                </Card>
              )}

              {/* Instructors */}
              {instructors.length > 0 && (
                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-heading font-bold text-[#1D1D1B] mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#F9B438]" />Formateur</h3>
                    {instructors.map(inst => (
                      <div key={inst.id} className="flex items-center gap-3 mt-3">
                        {inst.photo_url ? (
                          <img src={inst.photo_url} alt={inst.name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}><User className="w-6 h-6 text-[#1D1D1B]" /></div>
                        )}
                        <div>
                          <p className="font-medium text-[#1D1D1B]">{inst.name}</p>
                          {inst.title && <p className="text-sm text-neutral-400">{inst.title}</p>}
                          {inst.organization && <p className="text-xs text-neutral-400">{inst.organization}</p>}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Sessions */}
              {sessions.length > 0 && (
                <Card className="border-[#F9B438]">
                  <CardContent className="p-5">
                    <h3 className="font-heading font-bold text-[#1D1D1B] mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#F9B438]" />Prochaines sessions</h3>
                    <div className="space-y-3">
                      {sessions.map(s => (
                        <div key={s.id} className="p-3 bg-neutral-50 rounded-lg" data-testid={`session-${s.id}`}>
                          {s.session_title && <p className="font-medium text-sm text-[#1D1D1B]">{s.session_title}</p>}
                          <p className="text-sm text-neutral-600">{s.start_date}{s.end_date && s.end_date !== s.start_date ? ` au ${s.end_date}` : ''}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-neutral-400">
                            <span className="flex items-center gap-1"><Video className="w-3 h-3" />Microsoft Teams</span>
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.max_participants - (s.registered_count || 0)} places</span>
                          </div>
                          <Button size="sm" className="w-full mt-2" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)', color:'#1D1D1B'}}
                            onClick={() => { setForm({...form, session_id: s.id}); setShowForm(true); }}>
                            S'inscrire a cette session
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Certification */}
              {training.certification && (
                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-heading font-bold text-[#1D1D1B] mb-2 flex items-center gap-2"><Award className="w-4 h-4 text-[#F9B438]" />Certification</h3>
                    <p className="text-sm text-neutral-600">{training.certification}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <Card className="relative w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto" data-testid="registration-form">
            <CardContent className="p-6">
              <h2 className="font-heading text-xl font-bold text-[#1D1D1B] mb-1">Inscription</h2>
              <p className="text-sm text-neutral-400 mb-6">{training.title}</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Prenom *</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required data-testid="reg-first-name" /></div>
                  <div><Label>Nom *</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required data-testid="reg-last-name" /></div>
                </div>
                <div><Label>Courriel *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="reg-email" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Telephone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
                  <div><Label>Entreprise</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
                </div>
                <div><Label>No. membre OIQ (optionnel)</Label><Input value={form.professional_order_number} onChange={e => setForm({...form, professional_order_number: e.target.value})} placeholder="Ex: 12345" /></div>
                <div><Label>Nombre de participants</Label><Input type="number" min="1" value={form.num_participants} onChange={e => setForm({...form, num_participants: parseInt(e.target.value) || 1})} /></div>

                {sessions.length > 0 && (
                  <div>
                    <Label>Session</Label>
                    <select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={form.session_id} onChange={e => setForm({...form, session_id: e.target.value})} data-testid="reg-session">
                      <option value="">-- Selectionner une session --</option>
                      {sessions.map(s => <option key={s.id} value={s.id}>{s.session_title || s.start_date} ({s.max_participants - (s.registered_count || 0)} places)</option>)}
                    </select>
                  </div>
                )}

                {/* Payment info */}
                <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100" data-testid="payment-stripe-info">
                  <CreditCard className="w-5 h-5 text-[#F9B438] flex-shrink-0" />
                  <span className="text-sm text-neutral-600">Paiement securise par carte de credit (Stripe)</span>
                </div>

                <div><Label>Commentaires</Label><Textarea value={form.comments} onChange={e => setForm({...form, comments: e.target.value})} rows={2} placeholder="Questions ou besoins speciaux" /></div>

                {/* Price summary */}
                <div className="p-3 rounded-lg bg-neutral-50 flex items-center justify-between">
                  <span className="text-sm text-neutral-600">Total ({form.num_participants} participant{form.num_participants > 1 ? 's' : ''})</span>
                  <span className="text-lg font-bold text-[#1D1D1B]">{(price * form.num_participants).toFixed(2)} $ <span className="text-xs font-normal text-neutral-400">+ taxes</span></span>
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">Annuler</Button>
                  <Button type="submit" disabled={submitting} className="flex-1" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)', color:'#1D1D1B'}} data-testid="submit-registration">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" />Payer maintenant</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TrainingDetailPage;
